import React, { useState, useRef,useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const preFilledAnswers = {
  // --- Personal Information ---
  age: 59,
  sex: "Female",
  current_weight: 72,
  current_weight_unit: "metric",
  height: 165,
  height_unit: "metric",
  usual_weight: 75,
  usual_weight_unit: "metric",

  // --- Food Preferences Drill-Down ---
  favorite_food_groups: [
    "Vegetables",
    "Fruits",
    "Grains/Bread/Pasta",
    "Legumes (beans, lentils)",
    "Dairy",
    "Sweets/Desserts"
  ],
  favorite_food_details: {
    Vegetables: ["Broccoli", "Carrots", "Spinach", "Sweet potatoes", "Tomatoes"],
    Fruits: ["Berries", "Bananas", "Apples", "Oranges"],
    "Grains/Bread/Pasta": ["Brown rice", "Whole wheat bread", "Quinoa", "Oats"],
    "Legumes (beans, lentils)": ["Chickpeas", "Black beans", "Lentils"],
    Dairy: ["Greek yogurt", "Low-fat cheese"],
    "Sweets/Desserts": ["Dark chocolate", "Fruit salad"]
  },

  // --- Symptoms & Side Effects ---
  symptoms: "Yes",
  symptom_list: ["Fatigue", "Loss of appetite"],
  fluid_tolerance: "No issues",
  weight_changes: "Weight loss >5% in last month",

  // --- Cancer Treatment & Meds ---
  treatments: ["Chemotherapy", "Radiation therapy"],
  medications: ["Anti-nausea drugs (e.g., ondansetron)", "Corticosteroids (e.g., prednisone; monitor sodium/sugar)"],

  // --- GI & Dietary Restrictions ---
  gi_conditions: ["Gastroesophageal reflux disease (GERD)"],
  dietary_restrictions: ["Vegetarian", "Low sodium / low salt"],

  // --- Allergies ---
  food_allergies: ["Tree nuts"],
  allergy_severity: "Mild",
  cross_contamination: "No",

  // --- Supplements & Eating Habits ---
  supplements: ["Oral nutritional supplements (e.g., Ensure, Boost)"],
  meal_frequency: "4–6 smaller meals/snacks",
  kitchen_access: "Full kitchen access",

  // --- Preferences & Lifestyle ---
  food_preferences: "Bland or mild flavors",
  activity_level: "Moderate activity (moderate exercise/sports 3–5 days/week)",
  mental_health: "Yes, mildly",
  sleep_quality: "Somewhat disturbed sleep",

  // --- Fluids & Sugar ---
  fluid_intake: "1–2 liters",
  sugar_limit: "No",

  // --- Culture & Other ---
  cultural_preferences: "No",
  additional_info: "I prefer soft foods and avoid spicy dishes due to mouth sensitivity."
};


const loadingMessages = [
  "Personalizing your recommendations…",
  "Selecting delicious recipes…",
  "Balancing nutrition for your needs…",
  "Finalizing your meal plan…"
];

function PlanSkeleton() {
  return (
    <div className="max-w-lg w-full space-y-4 mb-6">
      <Skeleton height={30} width="60%" />
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} height={20} />
      ))}
    </div>
  );
}

export default function TestGeneratePlan() {
  const [answers] = useState(preFilledAnswers);
  const [generating, setGenerating] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [error, setError] = useState("");
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const [progress, setProgress] = useState(1);
  const navigate = useNavigate();
  const spinnerTimeout = useRef(null);
  const progressInterval = useRef(null);

  useEffect(() => {
    if (generating) {
      spinnerTimeout.current = setTimeout(() => setShowSpinner(true), 200);

      // Cycle loading messages every 5 seconds
      const msgInterval = setInterval(() => {
        setLoadingMsgIndex(i => (i + 1) % loadingMessages.length);
      }, 20000);

      // Progress bar: 1% to 100% in 60 seconds (600ms per 1%)
      setProgress(1);
      progressInterval.current = setInterval(() => {
        setProgress(prev => (prev < 95 ? prev + 1 : 95));
      }, 1200);

      return () => {
        clearTimeout(spinnerTimeout.current);
        clearInterval(msgInterval);
        clearInterval(progressInterval.current);
      };
    } else {
      setShowSpinner(false);
      setLoadingMsgIndex(0);
      setProgress(1);
      clearInterval(progressInterval.current);
    }
  }, [generating]);

  async function handleGenerate() {
    setGenerating(true);
    setError("");
    try {
      const res = await fetch("https://createmealplan-6w2wugyllq-uc.a.run.app/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userAnswers: answers, numDays: 7, email: 'tomas@tomur.lt'}),
      });
      if (!res.ok) throw new Error("Failed to generate plan. Please try again.");
      const data = await res.json();

      // Fast-forward progress bar to 100% and redirect after short delay
      setProgress(100);
      setTimeout(() => {
        setGenerating(false); // Optional: for safety
        navigate(`/plan/${data.planId}`);
      }, 300); // Short delay for smoothness
    } catch (err) {
      setError(err.message || "An error occurred.");
      setGenerating(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-[#F6F5EC]">
      <h1 className="text-3xl font-bold mb-6 text-[#4F5D2F]">Test Generate Plan</h1>
      <pre className="bg-white p-4 rounded shadow max-w-lg w-full mb-6 overflow-auto" style={{ fontSize: '14px' }}>
        {JSON.stringify(answers, null, 2)}
      </pre>

      {error && (
        <div className="text-red-600 mb-4">
          {error}
          <button
            onClick={handleGenerate}
            className="underline ml-2"
            disabled={generating}
          >
            Retry
          </button>
        </div>
      )}

      <button
        onClick={handleGenerate}
        disabled={generating}
        aria-busy={generating ? "true" : "false"}
        className="px-6 py-3 bg-[#A3B18A] hover:bg-[#769656] text-white rounded-lg font-semibold mb-6"
      >
        {generating ? "Generating your plan…" : "Generate Plan"}
      </button>

      {generating && showSpinner && (
        <div className="flex flex-col items-center w-full">
          <div className="mb-4">
            <svg className="animate-spin h-8 w-8 text-[#A3B18A]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
            </svg>
          </div>
          <div className="mb-2 text-[#4F5D2F] font-medium" style={{ minHeight: 24 }}>
            {loadingMessages[loadingMsgIndex]}
          </div>
          <div className="w-full max-w-lg mb-2">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-[#A3B18A] h-4 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-right text-sm text-[#4F5D2F] mt-1" style={{ minWidth: 40 }}>
              {progress}%
            </div>
          </div>
          <PlanSkeleton />
        </div>
      )}
    </div>
  );
}
